import { Children, cloneElement, useState, ReactNode, ReactElement, CSSProperties, ComponentType } from 'react'
import { noop } from 'lodash-es'
import {
  useFloating,
  flip,
  shift,
  autoUpdate,
  useInteractions,
  useClick,
  useDismiss,
  FloatingPortal,
  size,
  Placement,
  Middleware,
} from '@floating-ui/react'
import { getFloatingPortalProps } from '@utils/portal_target'

const WINDOW_PADDING = 16 // px padding from viewport edges when auto sizing

interface PopoverProps {
  children: ReactNode
  placement?: Placement
  // Controlled open state. If omitted the popover manages its own internal state.
  open?: boolean
  // Called when the popover requests a change to open state (reference click, dismiss, etc.)
  onOpenChange?: (open: boolean) => void
  // Whether clicking the reference (or pressing Space/Enter) should toggle the popover.
  // Set to false when the reference is a text input to avoid interfering with typing.
  triggerOnReferenceClick?: boolean
  onDismiss?: () => void
  onOpen?: () => void
  middleware?: Middleware[]
  autoSize?: boolean // enable dynamic maxHeight with scroll when content exceeds viewport
  viewportMargin?: number // extra visual margin from viewport edges when auto sizing
}

interface PopoverReferenceProps {
  className?: string
  children: ReactNode
  refs?: {
    setReference: (node: HTMLElement | null) => void
  }
  getReferenceProps?: () => Record<string, any>
  onTrigger?: (() => void) | null
}

const PopoverReference = ({
  children,
  className,
  refs,
  getReferenceProps,
  onTrigger = null,
}: PopoverReferenceProps) => {
  const childCount = Children.count(children)
  let referenceClickHandler = noop
  let content = children

  if (onTrigger !== null) {
    if (childCount === 0) {
      return null
    } else if (childCount > 1) {
      referenceClickHandler = onTrigger
      content = children
    } else {
      const childrenArray = Children.toArray(children)
      let child = childrenArray[0] as ReactElement
      if (child.props && (child.props.onClick === null || child.props.onClick === noop)) {
        child = cloneElement(child, {
          onClick: onTrigger,
        })
      }
      content = child
    }
  }

  const refProps = getReferenceProps && getReferenceProps()
  return (
    <span ref={refs?.setReference} onClick={referenceClickHandler} {...refProps} className={className}>
      {content}
    </span>
  )
}

PopoverReference.displayName = 'Popover.Reference'

interface PopoverContentProps {
  className?: string
  children: ReactNode
  refs?: {
    setFloating: (node: HTMLElement | null) => void
  }
  floatingStyles?: CSSProperties
  getFloatingProps?: () => Record<string, any>
  popoverVisible?: boolean
  renderInPortal?: boolean
}

const PopoverContent = ({
  children,
  className = 'popover popover--opaque p',
  refs,
  floatingStyles,
  getFloatingProps,
  popoverVisible = false,
  renderInPortal = true,
}: PopoverContentProps) => {
  const flProps = getFloatingProps && getFloatingProps()
  const content = (
    <div ref={refs?.setFloating} style={floatingStyles} {...flProps} className={className}>
      {children}
    </div>
  )

  if (popoverVisible) {
    if (renderInPortal) {
      return <FloatingPortal {...getFloatingPortalProps()}>{content}</FloatingPortal>
    }
    return content
  }
  return null
}

PopoverContent.displayName = 'Popover.Content'

type PopoverComponent = ComponentType<PopoverProps> & {
  Reference: ComponentType<PopoverReferenceProps>
  Content: ComponentType<PopoverContentProps>
}

const Popover: PopoverComponent = ({
  children,
  placement = 'bottom',
  open, // controlled prop
  onOpenChange,
  triggerOnReferenceClick = true,
  onDismiss,
  onOpen,
  middleware = [
    flip(),
    shift({
      padding: WINDOW_PADDING, // from window edges
    }),
  ],
  autoSize = true,
  viewportMargin = WINDOW_PADDING, // extra visual margin from viewport edges when auto sizing
}: PopoverProps) => {
  // internal state when uncontrolled
  const [internalOpen, setInternalOpen] = useState<boolean>(false)

  const isControlled = typeof open === 'boolean'
  const popoverVisible = isControlled ? (open as boolean) : internalOpen

  const handleVisibleChange = (isVisible: boolean) => {
    if (!isControlled) {
      setInternalOpen(isVisible)
    }
    if (onOpenChange) {
      onOpenChange(isVisible)
    }
    if (!isVisible && onDismiss) {
      onDismiss()
    }
    if (isVisible && onOpen) {
      onOpen()
    }
  }

  // Inject size middleware if autoSize is enabled and not already provided
  let effectiveMiddleware = middleware

  // If using default middleware array (i.e., caller didn't pass a custom one), replace shift padding with viewportMargin
  if (middleware === Popover.defaultProps?.middleware || middleware.length === 2) {
    const hasCustom = middleware.some(mw => mw && mw.name !== 'flip' && mw.name !== 'shift')
    if (!hasCustom) {
      effectiveMiddleware = [
        flip(),
        shift({
          padding: viewportMargin,
        }),
      ]
    }
  }
  if (autoSize) {
    const hasSize = effectiveMiddleware.some(mw => mw && mw.name === 'size')
    if (!hasSize) {
      effectiveMiddleware = [
        ...effectiveMiddleware,
        size({
          apply({ elements, availableHeight }) {
            // Reduce available height by viewportMargin to ensure a visible gap from the constrained edge
            const adjusted = Math.max(0, availableHeight - viewportMargin)
            Object.assign(elements.floating.style, {
              maxHeight: `${adjusted}px`,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            })
          },
        }),
      ]
    }
  }

  const { refs, floatingStyles, context } = useFloating({
    open: popoverVisible,
    onOpenChange: handleVisibleChange,
    placement,
    middleware: effectiveMiddleware,
    whileElementsMounted: autoUpdate,
  })
  const interactions = [] as any[]
  if (triggerOnReferenceClick) {
    interactions.push(useClick(context))
  }
  interactions.push(useDismiss(context))
  const { getReferenceProps, getFloatingProps } = useInteractions(interactions)

  const handleTrigger = () => {
    handleVisibleChange(!popoverVisible)
  }

  return (
    <>
      {Children.map(children, child =>
        cloneElement(child as ReactElement, {
          refs,
          floatingStyles,
          getFloatingProps,
          getReferenceProps,
          popoverVisible,
          onTrigger: triggerOnReferenceClick ? handleTrigger : null,
        })
      )}
    </>
  )
}

Popover.Reference = PopoverReference
Popover.Content = PopoverContent

export default Popover
